import AlertMinimalIcon from '@assets/icons/alert-minimal.svg';
import i18n from '@/utils/i18n';
import React, { useMemo, useRef } from 'react';
import { addToOrderPrebookItemDisabled } from '@/contexts/item-context-utils';
import { ApplicationType } from '@/contexts/dynamic-config';
import { Button } from '@/components/button';
import { ChatStatus } from '@/enums';
import { createStyles } from '@/contexts/theme-context';
import { EssentialItemButton } from './essential-item-button';
import { getAddedVariantPackEventPayloads } from '@/utils/ads';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { ItemsContext_OrderGuideItemFieldsFragment } from '@/types';
import { ItemSubtitleV2 } from '@/components/item-subtitle-v2';
import { Note } from '@/components/note';
import { Platform, Text, View } from 'react-native';
import { QuantitySelector } from '@/components/quantity-selector';
import { sharedStyles } from './item-detail-actions';
import { TYPOGRAPHY } from '@/styles';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import {
  useAddToOrderDisabled,
  useAvailabilityStatusEnabled,
} from '@/hooks/use-add-to-order-disabled';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useHover } from 'react-native-web-hooks';
import { useItemLabels } from '@/hooks/use-item-labels';
import { useItemVisibleQuantity } from '@/stores/new-order-quantity-store';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { usePriceEditing } from '@/hooks/use-price-editing';
import { useUpdateOrderGuideItemContext } from '@/contexts/update-order-guide-item-context';
import { useUserContext } from '@/contexts/user-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

interface Props {
  isEssentialItemButtonVisible: boolean;
  isFocused: boolean;
  item: ItemListOptionMetadata;
  metadata: ItemListOptionMetadata;
  orderGuideItem: ItemsContext_OrderGuideItemFieldsFragment | null;
  showTooltipOnMount?: boolean;
  updateQuantity: UpdateQuantityFn;
}

export const ListItemViewActions: React.FC<Props> = ({
  isEssentialItemButtonVisible,
  isFocused,
  item,
  metadata,
  orderGuideItem,
  showTooltipOnMount,
  updateQuantity,
}) => {
  const [styles] = useStyles();
  const noteContainerRef = useRef<View>(null);
  const editButtonRef = useRef<View>(null);
  const isPaneHovered = useHover(noteContainerRef);
  const isEditHovered = useHover(editButtonRef);
  const showEditOverlay =
    Platform.OS === 'web' && (isPaneHovered || isEditHovered);
  const visibleQuantity = useItemVisibleQuantity(item.variantPackUUID);
  const { customOrderGuideEditingEnabled, supplierTimezone } =
    useNewOrderContext();
  const { editItemNotes } = useUpdateOrderGuideItemContext();
  const { essentialVariantPackUUIDs } = useVariantPackInfoContext();
  const availabilityStatusEnabled = useAvailabilityStatusEnabled();
  const { isLargeLayout } = useLayoutContext();
  const {
    selectedRestaurantRelationship: { chatStatus },
  } = useUserContext();
  const isProspect = chatStatus === ChatStatus.PROSPECT;
  const isCatalogLimited =
    useDynamicConfigValue('application_type') === ApplicationType.CATALOG_ONLY;
  const isOrderNoteEnabled = useSupplierDynamicConfigValue(
    'line_item_notes_config',
  )?.enabled;

  const variantPackUUID = item.variantPackUUID;
  const isAddToOrderDisabled = useAddToOrderDisabled(item.variantPackUUID);
  const { isSalesRepPeakPriceProtectionInEffect } = usePriceEditing(
    item.variantPackUUID,
  );
  const isAddToOrderPrebookItemDisabled = useMemo(
    () => addToOrderPrebookItemDisabled(item, supplierTimezone),
    [item, supplierTimezone],
  );
  const itemLabels = useItemLabels(item, isSalesRepPeakPriceProtectionInEffect);
  const isEssential = essentialVariantPackUUIDs.has(variantPackUUID);

  const { memo, notes, request } = orderGuideItem?.order_guide_restaurant || {};
  const hasNotes = Boolean(isOrderNoteEnabled ? memo || request : notes);

  // Stable reference so the memoized ItemSubtitleV2 can bail out when this row
  // re-renders for an unrelated reason (e.g. its own quantity press).
  const subtitleStyle = useMemo(
    () => [
      sharedStyles.subtitle,
      (isAddToOrderDisabled || isAddToOrderPrebookItemDisabled) &&
      !availabilityStatusEnabled
        ? sharedStyles.outOfStockSubtitle
        : null,
    ],
    [
      isAddToOrderDisabled,
      isAddToOrderPrebookItemDisabled,
      availabilityStatusEnabled,
    ],
  );

  return (
    <View style={styles.base}>
      <View style={styles.itemSubtitleContainer}>
        <ItemSubtitleV2
          baseUnit={item.baseUnit}
          currencyCode={item.currencyCode}
          displayString={item.packDisplayString}
          labels={itemLabels}
          modifierString={item.modifierString}
          promotionPriceInMicros={item.promotionPricePerUnitInMicros}
          sku={item.externalID}
          style={subtitleStyle}
          unitCount={item.unitCount}
          unitPriceInMicros={item.unitPriceInMicros}
        />
      </View>
      <View style={styles.actionsContainer}>
        {customOrderGuideEditingEnabled ? (
          hasNotes && orderGuideItem ? (
            <View
              ref={noteContainerRef}
              style={isLargeLayout ? styles.notePaneLarge : styles.notePane}
            >
              {Platform.OS === 'web' ? (
                <>
                  <View
                    pointerEvents={showEditOverlay ? 'none' : 'auto'}
                    style={showEditOverlay ? styles.notePaneHidden : undefined}
                  >
                    <Note
                      onPress={(type) => editItemNotes(item, type)}
                      orderGuideItem={orderGuideItem}
                    />
                  </View>
                  <View
                    pointerEvents={showEditOverlay ? 'auto' : 'none'}
                    style={[
                      styles.notePaneOverlay,
                      !showEditOverlay && styles.notePaneHidden,
                    ]}
                  >
                    <Button
                      contentStyle={styles.noteEditButtonContent}
                      focusable={false}
                      hoverStyle={styles.noteActionTextHover}
                      onPress={() => editItemNotes(item)}
                      ref={editButtonRef}
                      size="small"
                      textProps={{ numberOfLines: 1 }}
                      textStyle={styles.noteActionText}
                      trackingData={{
                        name: 'order_guide_list_item.edit_note_button',
                        properties: { variantPackUUID },
                      }}
                      type="tertiary"
                    >
                      {i18n.t('edit_note')}
                    </Button>
                  </View>
                </>
              ) : (
                <Note
                  onPress={(type) => editItemNotes(item, type)}
                  orderGuideItem={orderGuideItem}
                />
              )}
            </View>
          ) : (
            <View style={styles.noteAddWrap}>
              <Button
                focusable={false}
                hoverStyle={styles.noteActionTextHover}
                onPress={() => editItemNotes(item)}
                size="small"
                textProps={{ numberOfLines: 1 }}
                textStyle={styles.noteActionText}
                trackingData={{
                  name: 'order_guide_list_item.add_note_button',
                  properties: { variantPackUUID },
                }}
                type="tertiary"
              >
                {i18n.t('add_note')}
              </Button>
            </View>
          )
        ) : null}
        {isEssentialItemButtonVisible ? (
          <EssentialItemButton
            itemMetadata={metadata}
            variantPackUUID={variantPackUUID}
          />
        ) : null}
        {!isCatalogLimited ? (
          <View style={styles.quantityContainer}>
            {isAddToOrderDisabled || isAddToOrderPrebookItemDisabled ? (
              availabilityStatusEnabled ? null : (
                <View style={sharedStyles.unavailable}>
                  <Text style={TYPOGRAPHY.pSemiBold}>
                    {i18n.t('unavailable')}
                  </Text>
                </View>
              )
            ) : !isProspect ? (
              <QuantitySelector
                additionalIcon={isEssential ? AlertMinimalIcon : null}
                canDecrementToZero
                isFocused={isFocused}
                maxQuantity={item.maximumQuantity}
                minQuantity={item.minimumQuantity}
                onChange={(previousQuantity: number, newQuantity: number) => {
                  updateQuantity({
                    eventPayloads: getAddedVariantPackEventPayloads(metadata),
                    newQuantity,
                    previousQuantity,
                    variantPackDetails: {
                      itemDisplayName: item.displayName,
                      metadata: item.variantPackMetadata,
                    },
                    variantPackUUID,
                  });
                }}
                quantity={visibleQuantity}
                showTooltipOnMount={showTooltipOnMount}
              />
            ) : null}
          </View>
        ) : null}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  actionsContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 16,
    justifyContent: 'space-between',
  },
  base: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  itemSubtitleContainer: {
    flex: 1,
  },
  noteActionText: {
    borderRadius: theme.sk.radii.radiusMedium,
    color: theme.colors.inactiveText,
    fontSize: theme.fontSizes.small,
    padding: theme.sk.spacing.spaceSmall,
  },
  noteActionTextHover: {
    backgroundColor: theme.sk.color.bg.button.tertiary.hover,
  },
  noteAddWrap: {
    alignSelf: 'flex-start',
  },
  noteEditButtonContent: {
    alignSelf: 'flex-end',
  },
  notePane: {
    maxWidth: 200,
    position: 'relative',
  },
  notePaneHidden: {
    opacity: 0,
  },
  notePaneLarge: {
    maxWidth: 400,
    position: 'relative',
  },
  notePaneOverlay: {
    alignItems: 'flex-end',
    bottom: 0,
    end: 0,
    justifyContent: 'center',
    position: 'absolute',
    start: 0,
    top: 0,
    zIndex: 1,
  },
  quantityContainer: {
    alignItems: 'flex-end',
    width: 138,
  },
}));
